import type { CommonEditorTab } from "./reducers";
import { useState } from "react";
import { NavbarTabContent } from "../shell/NavbarTabContent";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { useTranslation } from "react-i18next";
import { SidebarLoading } from "../shell/SidebarLoading";
import { assertTypeName } from "../client/guards";
import { DocumentExportCombobox } from "../documents/DocumentExportCombobox";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { Label } from "@acdc2/ui/components/access-typography";
import { DocumentExportArtifact } from "../documents/DocumentExportArtifact";
import {
  useCreateArtifactMutation,
  useNavbarExportTabContentQuery,
  type ExporterType,
  type NavbarExportTabContentFragment,
} from "../client/generated";

type Props = {
  fragment: NavbarExportTabContentFragment;
  isActive: boolean;
};

export function NavbarExportTabContent({ fragment, isActive }: Props) {
  const { t } = useTranslation();

  return (
    <NavbarTabContent
      value={"export" satisfies CommonEditorTab}
      title={t("NavbarExportTabContent.title")}
    >
      <Body fragment={fragment} isActive={isActive} />
    </NavbarTabContent>
  );
}

function Body({ fragment, isActive }: Props) {
  const { t } = useTranslation();
  const [exportType, setExportType] = useState<ExporterType>();
  const [createArtifact, { loading }] = useCreateArtifactMutation();

  const { data } = useNavbarExportTabContentQuery({
    variables: { documentId: fragment.id },
    pollInterval: 3000,
    skip: !isActive,
  });

  if (!data) return <SidebarLoading />;
  const { document } = data;
  assertTypeName(document?.__typename === "Document");

  const exportDocument = async () => {
    if (!exportType) return;
    await createArtifact({
      variables: {
        exportType,
        documentId: document.id,
      },
    });
  };

  return (
    <ScrollArea className="flex-1">
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-row gap-2">
          <DocumentExportCombobox value={exportType} onSelect={setExportType} />
          <AccessButton
            variant="outline"
            disabled={!exportType}
            onClick={exportDocument}
            loading={loading}
          >
            {t("NavbarExportTabContent.submit")}
          </AccessButton>
        </div>
        <div className="flex flex-col gap-4">
          <Label>{t("NavbarExportTabContent.artifacts.title")}</Label>
          {document.artifacts.artifacts.length > 0 ? (
            <ul className="flex flex-col gap-4">
              {document.artifacts.artifacts.map((artifact) => (
                <li key={artifact.id}>
                  <DocumentExportArtifact fragment={artifact} />
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-sm text-muted-foreground">
              {t("NavbarExportTabContent.artifacts.placeholder")}
            </span>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
